import { useEffect, useState } from "react";
import { Menu, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS, CALENDLY_URL } from "@/lib/constants";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a0a0f]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#top"
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
          >
            <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#00d4ff]/40 bg-[#00d4ff]/10">
              <Zap
                className="h-4 w-4 text-[#00d4ff] group-hover:scale-110 transition-transform"
                strokeWidth={2.5}
              />
            </span>
            <span className="font-display text-xl font-bold tracking-tight">
              AI <span className="text-[#00d4ff]">VOLTA</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={link.testId}
                className="text-sm text-zinc-300 hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex">
            <Button
              asChild
              data-testid="navbar-cta-book"
              className="rounded-full bg-[#00d4ff] text-black hover:bg-[#33ddff] font-medium px-5 shadow-[0_0_18px_rgba(0,212,255,0.35)] hover:shadow-[0_0_28px_rgba(0,212,255,0.55)] transition-all"
            >
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Book a Free Assessment
              </a>
            </Button>
          </div>

          <button
            type="button"
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div
            data-testid="navbar-mobile-menu"
            className="md:hidden pb-6 pt-2 border-t border-white/10"
          >
            <div className="flex flex-col gap-1 pt-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="px-2 py-3 text-sm text-zinc-300 hover:text-white"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="navbar-mobile-cta"
                className="mt-3 inline-flex h-11 items-center justify-center rounded-full bg-[#00d4ff] text-black font-medium"
              >
                Book a Free Assessment
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
